export type ReceiptPrintFormat = 'thermal' | 'letter';
export type ReceiptStatus = 'issued' | 'void';
export type ReceiptCurrency = 'BOB' | 'USD';
export type ReceiptPaymentMethod = 'cash' | 'check' | 'bank_transfer' | 'qr';

export interface ReceiptCategory {
  id: string;
  code: string;
  name: string;
  default_print_format: ReceiptPrintFormat;
  is_active: boolean;
  created_at: string;
}

export interface ReceiptConcept {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
}

export interface ReceiptLine {
  id: string;
  receipt_id: string;
  line_no: number;
  concept_id: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  concept?: { id: string; code: string; name: string } | null;
}

export interface ReceiptPayment {
  id: string;
  receipt_id: string;
  method: ReceiptPaymentMethod;
  amount: number;
  bank_account_id: string | null;
  check_number: string | null;
  voucher_photo_url: string | null;
}

export interface Receipt {
  id: string;
  category_id: string;
  number: number;
  year: number;
  issued_at: string;
  issued_by: string | null;
  status: ReceiptStatus;
  void_reason: string | null;
  voided_at: string | null;
  voided_by: string | null;

  payer_name: string;
  payer_ci: string | null;

  currency: ReceiptCurrency;
  exchange_rate: number | null;

  subtotal: number;
  discount: number;
  total: number;
  total_in_words: string;

  public_token: string;

  source_type: string | null;
  source_id: string | null;

  custom: Record<string, unknown>;

  created_at: string;
  updated_at: string | null;

  // Joined
  category?: ReceiptCategory;
  lines?: ReceiptLine[];
  payments?: ReceiptPayment[];
}

/** One line item as entered in the issue form, before server-side subtotal computation. */
export interface ReceiptLineInput {
  concept_id: string;
  description: string;
  quantity: string;
  unit_price: string;
}

/** One payment split as entered in the issue form. */
export interface ReceiptPaymentInput {
  method: ReceiptPaymentMethod;
  amount: string;
  bank_account_id: string;
  check_number: string;
  voucher_photo_url: string;
}

export interface IssueReceiptInput {
  category_id: string;
  payer_name: string;
  payer_ci: string;
  currency: ReceiptCurrency;
  exchange_rate: string;
  discount: string;
  source_type?: string | null;
  source_id?: string | null;
  minister_id?: string | null;
  custom?: Record<string, unknown>;
  lines: ReceiptLineInput[];
  payments: ReceiptPaymentInput[];
}

/** Per-receipt fields for the Afiliación category, stored in receipts.custom. */
export interface AffiliationCustom {
  adb_code: string;
  /** YYYY-MM-DD */
  affiliation_date: string;
  district: string;
  region: string;
}

/** Lean row for the receipts list: no lines/payments (the preview refetches the full receipt). */
export type ReceiptListItem = Pick<
  Receipt,
  'id' | 'year' | 'number' | 'status' | 'payer_name' | 'payer_ci' | 'issued_at' | 'total'
  | 'currency' | 'void_reason' | 'source_type' | 'source_id'
> & {
  category?: Pick<ReceiptCategory, 'id' | 'name' | 'default_print_format'>;
};

export interface ReceiptFilters {
  year: number;
  categoryId: string | null;
  status: ReceiptStatus | null;
}

/** Shape returned by the public get_public_receipt() RPC — no internal ids. */
export interface PublicReceipt {
  number: number;
  year: number;
  category_code: string;
  category_name: string;
  status: ReceiptStatus;
  void_reason: string | null;
  issued_at: string;
  payer_name: string;
  payer_ci: string | null;
  currency: ReceiptCurrency;
  exchange_rate: number | null;
  subtotal: number;
  discount: number;
  total: number;
  total_in_words: string;
  custom: Record<string, unknown> | null;
  lines: Array<{
    line_no: number;
    concept_name: string | null;
    description: string;
    quantity: number;
    unit_price: number;
    subtotal: number;
  }> | null;
  payments: Array<{ method: ReceiptPaymentMethod; amount: number }> | null;
}

/** One general number per year across all categories — no prefix or year in the printed number. */
export function formatReceiptNumber(number: number): string {
  return String(number).padStart(6, '0');
}

/**
 * Shape both print templates consume. The admin preview (from `Receipt`, a
 * joined table row) and the public /r/:token page (from `PublicReceipt`, a
 * flattened RPC result) map into this one shape, so the thermal and letter
 * templates are written once.
 */
export interface ReceiptPrintData {
  year: number;
  number: number;
  categoryCode: string;
  categoryName: string;
  /** Per-category extras (the Afiliación block lives here) */
  custom: Record<string, unknown>;
  status: ReceiptStatus;
  voidReason: string | null;
  issuedAt: string;
  payerName: string;
  payerCi: string | null;
  currency: ReceiptCurrency;
  exchangeRate: number | null;
  subtotal: number;
  discount: number;
  total: number;
  totalInWords: string;
  lines: Array<{
    lineNo: number;
    conceptName: string | null;
    description: string;
    quantity: number;
    unitPrice: number;
    subtotal: number;
  }>;
  payments: Array<{ method: ReceiptPaymentMethod; amount: number }>;
  publicUrl: string;
}

export function receiptToPrintData(receipt: Receipt, publicUrl: string): ReceiptPrintData {
  return {
    year: receipt.year,
    number: receipt.number,
    categoryCode: receipt.category?.code ?? '',
    categoryName: receipt.category?.name ?? '',
    custom: receipt.custom ?? {},
    status: receipt.status,
    voidReason: receipt.void_reason,
    issuedAt: receipt.issued_at,
    payerName: receipt.payer_name,
    payerCi: receipt.payer_ci,
    currency: receipt.currency,
    exchangeRate: receipt.exchange_rate,
    subtotal: receipt.subtotal,
    discount: receipt.discount,
    total: receipt.total,
    totalInWords: receipt.total_in_words,
    lines: (receipt.lines ?? []).map((l) => ({
      lineNo: l.line_no,
      conceptName: l.concept?.name ?? null,
      description: l.description,
      quantity: l.quantity,
      unitPrice: l.unit_price,
      subtotal: l.subtotal,
    })),
    payments: (receipt.payments ?? []).map((p) => ({ method: p.method, amount: p.amount })),
    publicUrl,
  };
}

export function publicReceiptToPrintData(pub: PublicReceipt, publicUrl: string): ReceiptPrintData {
  return {
    year: pub.year,
    number: pub.number,
    categoryCode: pub.category_code,
    categoryName: pub.category_name,
    custom: pub.custom ?? {},
    status: pub.status,
    voidReason: pub.void_reason,
    issuedAt: pub.issued_at,
    payerName: pub.payer_name,
    payerCi: pub.payer_ci,
    currency: pub.currency,
    exchangeRate: pub.exchange_rate,
    subtotal: pub.subtotal,
    discount: pub.discount,
    total: pub.total,
    totalInWords: pub.total_in_words,
    lines: (pub.lines ?? []).map((l) => ({
      lineNo: l.line_no,
      conceptName: l.concept_name,
      description: l.description,
      quantity: l.quantity,
      unitPrice: l.unit_price,
      subtotal: l.subtotal,
    })),
    payments: (pub.payments ?? []).map((p) => ({ method: p.method, amount: p.amount })),
    publicUrl,
  };
}
